/**
 * The centre of the metric colour system: the tone vocabulary, the ONLY tone→className maps, the
 * response-scoped context every judgment runs under, and the single dispatcher a column's verdict
 * flows through.
 *
 * Colour rule (brief): green = better than the account's 90-day average, yellow = average, red =
 * worse. Strength tracks distance on the account's own p5↔p95 scale. Discrete ROAS/doctrine/change
 * tones are gone for gradient metrics; advisory badges (frequency / spend share) stay separate.
 */
import {
    CREATIVE_METRIC_RAMP_DIRECTION,
    evaluateLimitedData,
    metricRampT,
    type ICreativeMetricRampKey,
    type IMetricAnchor,
    type IMetricAnchorStatus,
    type IMetricAnchors,
} from 'shared'

import type { ICreativePerformanceRow } from '@/api/creative-intelligence/get-creative-performance-query'
import {
    GRID_METRIC_BY_KEY,
    toneFor,
    type IGridMetricDef,
    type IGridMetricKey,
} from '@/features/creative-performance/creative-performance-metrics'
import { relativeToneForColumn } from '@/features/creative-performance/creative-performance-relative-tone'

function rowHasVideoDelivery(row: ICreativePerformanceRow) {
    return row.creativeType === 'VIDEO' || row.thumbnailIsVideo === true || row.media?.kind === 'VIDEO'
}

/**
 * Metric-cell sentiment for advisory badges that are NOT the account-relative gradient
 * (frequency fatigue, spend-share concentration). Gradient metrics use `ramp` only.
 */
export type IMetricTone = 'good' | 'warn' | 'bad' | 'neutral'

/** A verdict's tone plus the one-line reason behind it — surfaced as the cell's tooltip. */
export interface IMetricToneResult {
    tone: IMetricTone
    reason: string
    /**
     * Continuous account-relative tint in [-1, 1] when W/G/R anchors apply.
     * +1 = full green, 0 = yellow (average), −1 = full red.
     */
    ramp?: number
    /** Why a gradient cell stayed grey — never blank. */
    greyReason?: 'limited_data' | 'learning' | 'unavailable'
}

/**
 * The verdict as the value's ink. `neutral` is deliberately `undefined`: an uncoloured number is
 * "judged and flat", not missing, and each surface keeps its own default ink.
 */
export const METRIC_TONE_INK: Record<IMetricTone, string | undefined> = {
    good: 'text-emerald-900',
    warn: 'text-amber-900',
    bad: 'text-red-800',
    neutral: undefined,
}

/**
 * Soft pill for advisory badges. Gradient cells use `metricRampValueClass` + wash instead.
 */
export const METRIC_TONE_PILL: Record<IMetricTone, string | undefined> = {
    good: 'bg-emerald-500/25 text-emerald-900',
    warn: 'bg-amber-500/25 text-amber-900',
    bad: 'bg-red-500/25 text-red-800',
    neutral: undefined,
}

/** Shape shared by every toned value pill — card, chip, table cell, panel. Squared-off, tight. */
export const METRIC_TONE_PILL_SHAPE = 'rounded-md px-1.5 py-px'

/** Dark fixed ink on a gradient wash — readable in dark mode and without colour vision. */
export const METRIC_RAMP_INK = 'text-ink'

/**
 * Classes for a discrete-tone value. Prefer this over composing ink alone so every surface
 * gets the soft pill. Returns `undefined` for neutral / missing tone.
 */
export function metricTonePillClass(tone: IMetricTone | undefined): string | undefined {
    if (!tone || tone === 'neutral') return undefined
    const wash = METRIC_TONE_PILL[tone]
    return wash ? `${METRIC_TONE_PILL_SHAPE} ${wash}` : undefined
}

/**
 * Classes for an account-relative ramp value — pill shape + dark ink. Pair with
 * `metricRampBackground(ramp)` as inline style.
 */
export function metricRampValueClass(ramp: number | undefined): string | undefined {
    if (metricRampBackground(ramp) === undefined) return undefined
    return `${METRIC_TONE_PILL_SHAPE} ${METRIC_RAMP_INK}`
}

const YELLOW = { r: 245, g: 158, b: 11 } // amber-500
const GREEN = { r: 16, g: 185, b: 129 } // emerald-500
const RED = { r: 239, g: 68, b: 68 } // red-500
const FULL_OPACITY = 0.32

function mixChannel(a: number, b: number, t: number) {
    return Math.round(a + (b - a) * t)
}

/**
 * Yellow↔green / yellow↔red wash from signed `t` in [-1, 1]. At t=0 the wash is a light yellow
 * (at average). Roughly 25–35% opacity at full strength so a dense grid does not become a heat map.
 */
export function metricRampBackground(t: number | undefined): string | undefined {
    if (t === undefined || !Number.isFinite(t)) return undefined
    const magnitude = Math.min(1, Math.abs(t))
    // Always show a wash when the cell is on the gradient, including exactly at average.
    const end = t >= 0 ? GREEN : RED
    const mixed = {
        r: mixChannel(YELLOW.r, end.r, magnitude),
        g: mixChannel(YELLOW.g, end.g, magnitude),
        b: mixChannel(YELLOW.b, end.b, magnitude),
    }
    const opacity = 0.14 + magnitude * (FULL_OPACITY - 0.14)
    return `rgba(${mixed.r}, ${mixed.g}, ${mixed.b}, ${opacity.toFixed(3)})`
}

/**
 * Response-level facts a judgment runs under, stamped onto the row projection before any tone
 * function sees it. Every field is optional and `undefined` reads as do-not-withhold — a stale
 * cached response renders exactly what it rendered before the stamp existed.
 */
export interface IMetricToneContext {
    /** Whether the ACCOUNT's terminal event is a purchase — kept for legacy absolute helpers. */
    isPurchaseOutcome?: boolean
    /**
     * True when the view thinned on an adaptive floor below the house floor. Absolute ROAS/CPA
     * grid verdicts used to read this; gradient colouring does not.
     */
    verdictFloorLowered?: boolean
    /** The account's average order value in micros — kept for non-colour AOV hover context. */
    accountAovMicros?: number | null
    /** Trailing-90d W/G/R anchors for account-relative ramps. */
    metricAnchors?: IMetricAnchors
    metricAnchorStatus?: IMetricAnchorStatus
    referenceCostPerResultMicros?: number | null
    accountRelevantEvents?: number
    metricAnchorCohortLabel?: string
    /**
     * The ACCOUNT's terminal action type — the event the anchor SQL divides by. Used by
     * `costPerResultAllowed` to withhold the CPR ramp when the row's `resultActionType` names
     * a different event: colouring against a terminal-based anchor otherwise puts the row on a
     * scale built from a quantity it does not count.
     */
    accountTerminalActionType?: string | null
}

/** The context merged onto a row — what every tone function may (optionally) read. */
export function stampToneContext<T extends object>(row: T, context: IMetricToneContext) {
    return { ...row, ...context }
}

/**
 * The account's own average order value from the grid's outcome model, in micros — the same
 * derivation the detail read ships as `accountAovMicros`.
 */
export function accountAovMicrosFromOutcomeModel(
    model: { hasValue: boolean; terminalCount: number; terminalValueMicros: string } | null | undefined
) {
    if (!model?.hasValue || !(model.terminalCount > 0)) return null
    return Math.round(Number(model.terminalValueMicros) / model.terminalCount)
}

type IJudgedRow = ICreativePerformanceRow & IMetricToneContext

/** Row value in the same unit as the warehouse anchor for this ramp key. */
function rampValue(columnId: ICreativeMetricRampKey, row: IJudgedRow): number | null {
    switch (columnId) {
        case 'roas':
            return asFinite(row.roas)
        case 'cpa':
            return micros(row.costPerPurchaseMicros)
        case 'costPerResult':
            return micros(row.costPerResultMicros)
        case 'outboundCtr':
            return asFinite(row.outboundCtr)
        case 'costPerOutboundClick':
            return ratioMicros(row.spendMicros, row.outboundClicks)
        case 'cpm':
            return row.cpmMicros == null ? null : Number(row.cpmMicros) / 1_000_000
        case 'landingPageViewRate':
            return asFinite(row.landingPageViewRate)
        case 'costPerLandingPageView':
            return ratioMicros(row.spendMicros, row.landingPageViews)
        case 'addToCartRate':
            return asFinite(row.addToCartRate)
        case 'costPerAddToCart':
            return micros(row.costPerAddToCartMicros)
        case 'conversionRate':
            return asFinite(row.conversionRate)
        case 'revenuePerVisitor':
            return micros(row.revenuePerVisitorMicros)
        case 'hookRate':
            return asFinite(row.hookRate)
        case 'holdRate':
            return asFinite(row.holdRate)
        case 'hookToClick':
            return ratio(row.outboundClicks, row.threeSecondPlays)
        case 'firstFrameRetention':
            return asFinite(row.firstFrameRetention)
        case 'retentionP100':
            return asFinite(row.retentionP100)
        case 'costPerThruPlay':
            return micros(row.costPerThruPlayMicros)
        case 'engagementRate':
            return asFinite(row.engagementRate)
        case 'costPerEngagement':
            return micros(row.costPerEngagementMicros)
        case 'costPerLeads':
            return micros(row.eventMetrics?.leads?.costMicros)
        case 'clickToPurchase':
            return ratio(row.terminalConversions ?? row.purchaseConversions, row.outboundClicks)
        case 'checkoutCompletionRate':
            return asFinite(row.checkoutCompletionRate)
        case 'cartToPurchase':
            return ratio(row.terminalConversions ?? row.purchaseConversions, row.addsToCart)
        default:
            return null
    }
}

function asFinite(v: number | null | undefined) {
    return v === null || v === undefined || !Number.isFinite(v) ? null : v
}

function micros(v: string | number | null | undefined) {
    if (v === null || v === undefined) return null
    const n = typeof v === 'number' ? v : Number(v)
    return Number.isFinite(n) ? n : null
}

function ratio(num: number | null | undefined, den: number | null | undefined) {
    if (num === null || num === undefined || den === null || den === undefined) return null
    if (!(den > 0) || !Number.isFinite(num) || !Number.isFinite(den)) return null
    return num / den
}

function ratioMicros(spendMicros: string | number | null | undefined, den: number | null | undefined) {
    const spend = micros(spendMicros)
    if (spend === null || den === null || den === undefined || !(den > 0)) return null
    return spend / den
}

function greyResult(reason: NonNullable<IMetricToneResult['greyReason']>, message: string): IMetricToneResult {
    return { tone: 'neutral', reason: message, greyReason: reason }
}

/**
 * Selected-range sufficiency for the row being coloured. Anchors ignore the selected range; the
 * value being coloured does not.
 */
function rowSufficiency(row: IJudgedRow) {
    const accountEvents = row.accountRelevantEvents
    if (accountEvents === undefined) return 'sufficient_data' as const
    const terminal = Number(row.terminalConversions ?? row.purchaseConversions ?? 0)
    const spend = Number(row.spendMicros ?? 0)
    const cpr = micros(row.costPerResultMicros ?? row.costPerPurchaseMicros)
    return evaluateLimitedData({
        accountRelevantEvents: accountEvents,
        creativeRelevantEvents: terminal,
        creativeImpressions: Number(row.impressions ?? 0),
        creativeSpend: spend,
        creativeCostPerResult: cpr,
        referenceCostPerResult: row.referenceCostPerResultMicros ?? null,
    })
}

/** Cost-per-result colours only when the result is the account terminal event and the row is not mixed. */
function costPerResultAllowed(row: IJudgedRow) {
    if (row.mixedObjectives) return false
    if (row.resultActionType == null) return false
    // Actively compare the row's result event against the account terminal. Without this a
    // single-objective row whose result is a DIFFERENT event than the account terminal would still
    // be coloured against the terminal-based `cpr_w/g/r` anchor.
    if (row.accountTerminalActionType != null && row.resultActionType !== row.accountTerminalActionType) {
        return false
    }
    return true
}

/**
 * Account-relative ramp when anchors exist. Video-scoped anchors withhold on statics. Returns
 * `undefined` when unbenchmarked or the cell has no measurable value.
 */
export function rampForColumn(columnId: string, row: IJudgedRow): number | undefined {
    if (!(columnId in CREATIVE_METRIC_RAMP_DIRECTION)) return undefined
    const key = columnId as ICreativeMetricRampKey

    if (row.metricAnchorStatus === 'unavailable') return undefined
    if (row.metricAnchorStatus === 'learning') return undefined

    const sufficiency = rowSufficiency(row)
    if (sufficiency === 'learning' || sufficiency === 'limited_data') return undefined

    if (key === 'costPerResult' && !costPerResultAllowed(row)) return undefined

    const anchor: IMetricAnchor | undefined = row.metricAnchors?.[key]
    if (!anchor) return undefined
    if (anchor.format === 'video' && !rowHasVideoDelivery(row)) return undefined

    const value = rampValue(key, row)
    // Finite values including zero rates colour; null (no denominator / withheld) does not.
    if (value === null) return undefined

    return metricRampT(value, anchor)
}

function greyForColumn(columnId: string, row: IJudgedRow): IMetricToneResult | null {
    if (!(columnId in CREATIVE_METRIC_RAMP_DIRECTION)) return null

    if (row.metricAnchorStatus === 'unavailable') {
        return greyResult('unavailable', 'Benchmark unavailable — account average could not be loaded.')
    }
    if (row.metricAnchorStatus === 'learning') {
        return greyResult('learning', 'Account still learning — fewer than 5 relevant events in the last 90 days.')
    }

    const sufficiency = rowSufficiency(row)
    if (sufficiency === 'learning') {
        return greyResult('learning', 'Account still learning — fewer than 5 relevant events in the last 90 days.')
    }
    if (sufficiency === 'limited_data') {
        return greyResult(
            'limited_data',
            'Limited data — fewer than 1,000 impressions and fewer than 5 relevant events.'
        )
    }

    const key = columnId as ICreativeMetricRampKey
    if (key === 'costPerResult' && !costPerResultAllowed(row)) {
        return greyResult('unavailable', 'Cost per result is only coloured when the result is the account terminal event.')
    }

    const anchor = row.metricAnchors?.[key]
    if (!anchor) {
        return greyResult('unavailable', 'Benchmark unavailable — not enough eligible ads in the cohort yet.')
    }
    if (anchor.format === 'video' && !rowHasVideoDelivery(row)) {
        return greyResult('unavailable', 'Video metric — static ads stay uncoloured.')
    }
    if (rampValue(key, row) === null) return null

    return null
}

function rampReason(columnId: string, row: IJudgedRow, t: number): string {
    const key = columnId as ICreativeMetricRampKey
    const anchor = row.metricAnchors?.[key]
    const cohort = row.metricAnchorCohortLabel ?? 'your Meta ads, 90 days'
    if (!anchor) return 'Coloured against your account average.'
    const n = anchor.n
    if (t > 0.05) {
        return `Better than your account's 90-day average (${cohort}, ${n} ads). Stronger colour = further from average.`
    }
    if (t < -0.05) {
        return `Worse than your account's 90-day average (${cohort}, ${n} ads). Stronger colour = further from average.`
    }
    return `About your account's 90-day average (${cohort}, ${n} ads).`
}

function withRamp(
    columnId: string,
    row: IJudgedRow,
    verdict: IMetricToneResult | null | undefined
): IMetricToneResult | null {
    // Gradient metrics: ramp owns the colour. Advisory discrete tones only apply to non-ramp columns.
    if (columnId in CREATIVE_METRIC_RAMP_DIRECTION) {
        const ramp = rampForColumn(columnId, row)
        if (ramp !== undefined) {
            return { tone: 'neutral', reason: rampReason(columnId, row, ramp), ramp }
        }
        return greyForColumn(columnId, row)
    }
    return verdict ?? null
}

/**
 * The one dispatcher for a column's verdict: grid-metric ids go to the absolute tone model,
 * everything else to the relative policy, then account-relative ramp replaces colour for gradient
 * metrics. A column neither knows answers `undefined`.
 */
export function toneForTableColumn(columnId: string) {
    const gridDef = GRID_METRIC_BY_KEY[columnId as IGridMetricKey] as IGridMetricDef | undefined
    if (gridDef) {
        return (row: ICreativePerformanceRow) =>
            withRamp(
                columnId,
                row as IJudgedRow,
                columnId in CREATIVE_METRIC_RAMP_DIRECTION ? null : toneFor(gridDef, row)
            )
    }
    const relative = relativeToneForColumn(columnId)
    if (!relative && !(columnId in CREATIVE_METRIC_RAMP_DIRECTION)) return undefined
    return (row: ICreativePerformanceRow) =>
        withRamp(
            columnId,
            row as IJudgedRow,
            columnId in CREATIVE_METRIC_RAMP_DIRECTION ? null : (relative?.(row) ?? null)
        )
}

/** Header tooltip — verbatim from the brief. */
export const METRIC_GRADIENT_HEADER_TOOLTIP =
    "Green = better than your account's 90-day average, yellow = average, red = worse. Stronger colour = further from average. Grey = not enough data yet. Hover for the numbers."
